import Button from "@/components/base/Button";

export interface SymptomItem {
  icon: string;
  title: string;
}

interface CareSymptomsProps {
  eyebrow: string;
  title: string;
  description: string;
  items: SymptomItem[];
  image: string;
  imageAlt: string;
  note?: string;
  ctaLabel?: string;
  ctaTo?: string;
  tone?: "light" | "tinted";
}

/**
 * "When should you seek care?" section — symptom cards beside a supportive
 * clinical image, with a clear specialist consultation CTA. Symptoms are framed
 * as general guidance, never as a diagnosis.
 */
export default function CareSymptoms({
  eyebrow,
  title,
  description,
  items,
  image,
  imageAlt,
  note = "These signs are general guidance only and are not a diagnosis. If they persist or worsen, a specialist assessment is worthwhile.",
  ctaLabel = "Consult a Specialist",
  ctaTo = "/appointment",
  tone = "tinted",
}: CareSymptomsProps) {
  const background = tone === "tinted" ? "bg-background-100" : "bg-background-50";

  return (
    <section className={`${background} py-16 md:py-24`}>
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-stretch gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="reveal lg:col-span-5">
            <div className="relative h-full min-h-[320px] overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              <img
                src={image}
                alt={imageAlt}
                title={imageAlt}
                className="h-full w-full object-cover object-top"
              />
              <div
                className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-primary-950/10 to-transparent"
                aria-hidden="true"
              ></div>
              <p className="absolute inset-x-0 bottom-0 p-5 font-body text-xs leading-relaxed text-background-100 md:p-6">
                <i className="ri-information-line mr-1.5 align-middle text-accent-300" aria-hidden="true"></i>
                {note}
              </p>
            </div>
          </div>

          <div className="reveal lg:col-span-7">
            <span className="inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.18em] text-accent-700">
              <span className="h-px w-8 bg-accent-400" aria-hidden="true"></span>
              {eyebrow}
            </span>
            <h2 className="mt-5 font-heading text-3xl font-semibold leading-[1.14] text-foreground-950 md:text-4xl">
              {title}
            </h2>
            <p className="mt-4 max-w-2xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
              {description}
            </p>

            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {items.map((item) => (
                <li
                  key={item.title}
                  className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 px-4 py-3.5"
                >
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${item.icon} text-base`} aria-hidden="true"></i>
                  </span>
                  <span className="font-body text-sm font-medium text-foreground-800">{item.title}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <Button to={ctaTo} size="lg" icon="ri-arrow-right-line">
                {ctaLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}